import * as echarts from 'echarts';
import React, { useEffect, useRef, useState } from 'react';
import 'echarts-gl';
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Paper,
} from '@mui/material';
import { BarChart3, Filter, X } from 'lucide-react';
import {
  Applicant,
  CandibankJobApplicantRequest,
  Skill,
} from '../../../utils/types/JobOpenings';
import { GraphFilterDrawer } from './GraphFilterDrawer';

interface AnalyticsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  totalCandidates: number;
  searchObject: CandibankJobApplicantRequest;
  setSearchObject: (value: (((prevState: CandibankJobApplicantRequest) => CandibankJobApplicantRequest) | CandibankJobApplicantRequest)) => void;
  candidates: Applicant[];
  loading: boolean;
  setLoading: (value: (((prevState: boolean) => boolean) | boolean)) => void;
  extractedSkill: Skill[];
  setExtractedSkill: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void;
  extractedJobTitles: Skill[];
  setExtractedJobTitles: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void;
  extractedKeywords: Skill[];
  setExtractedKeywords: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void;
  extractedBehavioralScore: Skill[];
  setExtractedBehavioralScore: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void;
}

export const AnalyticsDialog: React.FC<AnalyticsDialogProps> = ({
  isOpen,
  onClose,
  searchObject,
  setSearchObject,
  candidates,
  loading,
  setLoading,
  extractedSkill,
  setExtractedSkill,
  extractedJobTitles,
  setExtractedJobTitles,
  extractedKeywords,
  setExtractedKeywords,
  extractedBehavioralScore,
  setExtractedBehavioralScore,
}) => {
  const [viewType, setViewType] = useState<'2d-chart' | '3d-chart'>('2d-chart');
  const [isAnalyticalFilter, setIsAnalyticalFilter] = useState(false);
  const chart2DRef = useRef<HTMLDivElement>(null);
  const chart3DRef = useRef<HTMLDivElement>(null);
  const chartInstance2D = useRef<echarts.ECharts | null>(null);
  const chartInstance3D = useRef<echarts.ECharts | null>(null);
  const [similarToggle, setSimilarToggle] = useState<boolean>(false);
  const [weightingEnabled, setWeightingEnabled] = useState<boolean>(false);
  const [similarJobTitle, setSimilarJobTitle] = useState<boolean>(false);
  const [similarBehavior, setSimilarBehavior] = useState<boolean>(false);
  const [keywordToggle, setKeywordtoggle] = useState<boolean>(false);
  const [keywordWeightage, setKeywordWeightage] = useState<boolean>(true);
  const [useSkillPercentage, setUseSkillPercentage] = useState<boolean>(false);
  const [minMaxAge, setMinMaxAge] = useState<number[]>([0, 100]);
  const [statusList] = useState<string[]>([]);
  const [ageToggle, setAgeToggle] = useState<boolean>(true);
  const [genderList, setGenderList] = useState<string[]>([]);

  // Initialize 2D Chart
  useEffect(() => {
    if (viewType === '2d-chart' && chart2DRef.current && candidates.length > 0) {
      const timer = setTimeout(() => {
        if (chartInstance2D.current) {
          chartInstance2D.current.dispose();
          chartInstance2D.current = null;
        }

        chartInstance2D.current = echarts.init(chart2DRef.current);

        // Dynamically compute axis ranges from actual data
        const xValues = candidates.map(c => c.value[0]);
        const yValues = candidates.map(c => c.value[1]);
        const zValues = candidates.map(c => c.value[2]);

        const xMin = Math.floor(Math.min(...xValues));
        const xMax = Math.ceil(Math.max(...xValues));
        const yMin = Math.floor(Math.min(...yValues));
        const yMax = Math.ceil(Math.max(...yValues));

        const option = {
          title: {
            text: '2D Scatter: Skills vs Experience',
            left: 'center',
            top: 20,
            textStyle: {
              fontSize: 18,
              fontWeight: 'bold',
              color: '#334155',
            },
          },
          tooltip: {
            trigger: 'item',
            formatter: function (params: any) {
              const data = params.data;
              const candidate = candidates[params.dataIndex];
              return `<span style="display:block; padding: 8px;">
                            <strong>${candidate?.name || `Candidate ${params.dataIndex + 1}`}</strong><br/>
                            Skills: ${data[0]}<br/>
                            Experience: ${data[1]} years<br/>
                            Behavior Count: ${data[2]}
                        </span>`;
            },
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderColor: '#e2e8f0',
            borderWidth: 1,
            textStyle: { color: '#334155' },
          },
          grid: {
            left: '8%',
            right: '20%',
            top: '15%',
            bottom: '15%',
            containLabel: true,
          },
          xAxis: {
            type: 'value',
            name: 'Skills',
            nameLocation: 'center',
            nameGap: 40,
            nameTextStyle: {
              color: '#475569',
              fontSize: 14,
              fontWeight: 'bold',
            },
            min: xMin,
            max: xMax,
            splitLine: {
              show: true,
              lineStyle: { color: '#e2e8f0', opacity: 0.8 },
            },
            axisLabel: { color: '#64748b', fontSize: 12 },
          },
          yAxis: {
            type: 'value',
            name: 'Experience (Years)',
            nameLocation: 'center',
            nameGap: 50,
            nameTextStyle: {
              color: '#475569',
              fontSize: 14,
              fontWeight: 'bold',
            },
            min: yMin,
            max: yMax,
            splitLine: {
              show: true,
              lineStyle: { color: '#e2e8f0', opacity: 0.8 },
            },
            axisLabel: { color: '#64748b', fontSize: 12 },
          },
          visualMap: {
            min: yMin,
            max: yMax,
            dimension: 1,
            orient: 'vertical',
            right: 20,
            top: 80,
            text: ['High', 'Low'],
            calculable: true,
            textStyle: {
              color: '#64748b',
            },
            inRange: {
              color: ['#ef4444', '#f59e0b', '#3b82f6', '#10b981'],
            },
          },
          series: [{
            name: 'Candidates',
            type: 'scatter',
            data: candidates.map(c => [c.value[0], c.value[1], c.value[2]]),
            symbolSize: function () {
              return 25;
            },
            itemStyle: {
              opacity: 0.8,
              borderWidth: 2,
              borderColor: '#ffffff',
            },
            emphasis: {
              itemStyle: {
                opacity: 1,
                borderWidth: 3,
                shadowBlur: 10,
                shadowColor: 'rgba(0,0,0,0.3)',
              },
              scale: 1.2,
            },
            animation: true,
            animationDuration: 1200,
            animationEasing: 'cubicOut',
          }],
        };

        chartInstance2D.current.setOption(option, true);
        chartInstance2D.current.resize();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [viewType, candidates]);

  // Initialize 3D Chart
  useEffect(() => {
    if (viewType === '3d-chart' && chart3DRef.current && candidates.length > 0) {
      const timer = setTimeout(() => {
        if (chartInstance3D.current) {
          chartInstance3D.current.dispose();
          chartInstance3D.current = null;
        }
        try {
          chartInstance3D.current = echarts.init(chart3DRef.current, null, {
            renderer: 'canvas',
          });
        } catch {
          return;
        }

        const xValues = candidates.map(c => c.value[0]);
        const yValues = candidates.map(c => c.value[1]);
        const zValues = candidates.map(c => c.value[2]);

        const xMin = Math.floor(Math.min(...xValues));
        const xMax = Math.ceil(Math.max(...xValues));
        const yMin = Math.floor(Math.min(...yValues));
        const yMax = Math.ceil(Math.max(...yValues));
        const zMin = Math.floor(Math.min(...zValues));
        const zMax = Math.ceil(Math.max(...zValues));

        const option = {
          title: {
            text: '3D Scatter: Skills Score vs Experience vs Match Score',
            left: 'center',
            top: 20,
            textStyle: {
              fontSize: 18,
              fontWeight: 'bold',
              color: '#334155',
            },
          },
          tooltip: {
            trigger: 'item',
            formatter: function (params: any) {
              const data = params.data;
              const candidate = candidates[params.dataIndex];
              return `<span style="display:block; padding: 8px;">
                            <strong>${candidate?.name || `Candidate ${params.dataIndex + 1}`}</strong><br/>
                            Skills: ${data[0]}<br/>
                            Experience: ${data[1]} years<br/>
                            Behavior Count: ${data[2]}
                        </span>`;
            },
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderColor: '#e2e8f0',
            borderWidth: 1,
            textStyle: { color: '#334155' },
          },
          visualMap: {
            min: yMin,
            max: yMax,
            dimension: 1,
            orient: 'vertical',
            right: 20,
            top: 80,
            text: ['High', 'Low'],
            calculable: true,
            textStyle: { color: '#64748b' },
            inRange: {
              color: ['#ef4444', '#f59e0b', '#3b82f6', '#10b981'],
            },
          },
          xAxis3D: {
            type: 'value',
            name: 'Skills',
            nameTextStyle: {
              color: '#1e293b',
              fontSize: 14,
              fontWeight: 'bold',
            },
            min: xMin,
            max: xMax,
            axisLabel: {
              textStyle: {
                color: '#334155',
                fontSize: 12,
                fontWeight: '500',
              },
            },
            splitLine: {
              show: true,
              lineStyle: { color: '#cbd5e1', opacity: 0.8 },
            },
          },
          yAxis3D: {
            type: 'value',
            name: 'Experience (Years)',
            nameTextStyle: {
              color: '#1e293b',
              fontSize: 14,
              fontWeight: 'bold',
            },
            min: yMin,
            max: yMax,
            axisLabel: {
              textStyle: {
                color: '#334155',
                fontSize: 12,
                fontWeight: '500',
              },
            },
            splitLine: {
              show: true,
              lineStyle: { color: '#cbd5e1', opacity: 0.8 },
            },
          },
          zAxis3D: {
            type: 'value',
            name: 'Behavior Count',
            nameTextStyle: {
              color: '#1e293b',
              fontSize: 14,
              fontWeight: 'bold',
            },
            min: zMin,
            max: zMax,
            axisLabel: {
              textStyle: {
                color: '#334155',
                fontSize: 12,
                fontWeight: '500',
              },
            },
            splitLine: {
              show: true,
              lineStyle: { color: '#cbd5e1', opacity: 0.8 },
            },
          },
          grid3D: {
            boxWidth: 180,
            boxHeight: 120,
            boxDepth: 100,
            show: true,
            environment: 'none',
            viewControl: {
              projection: 'perspective',
              autoRotate: false,
              rotateSensitivity: 2,
              zoomSensitivity: 2,
              panSensitivity: 2,
              alpha: 25,
              beta: 45,
              distance: 280,
              minDistance: 150,
              maxDistance: 400,
            },
            light: {
              main: {
                intensity: 1.5,
                shadow: true,
                shadowQuality: 'medium',
                alpha: 30,
                beta: 40,
              },
              ambient: { intensity: 0.4 },
            },
            postEffect: {
              enable: true,
              SSAO: {
                enable: true,
                quality: 'medium',
                radius: 2,
              },
            },
          },
          series: [{
            name: 'Candidates',
            type: 'scatter3D',
            data: candidates.map(c => [c.value[0], c.value[1], c.value[2]]),
            symbolSize: 25,
            itemStyle: {
              opacity: 0.8,
              borderWidth: 2,
              borderColor: '#ffffff',
            },
            emphasis: {
              itemStyle: {
                opacity: 1,
                borderWidth: 3,
                borderColor: '#ffffff',
              },
            },
            animation: true,
            animationDurationUpdate: 1500,
            animationEasingUpdate: 'cubicInOut',
          }],
        };

        try {
          chartInstance3D.current.setOption(option, true);
          chartInstance3D.current.resize();
        } catch (error) {
          console.error('Failed to set 3D chart option:', error);
        }
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [viewType, candidates]);

  // Cleanup charts on unmount
  useEffect(() => {
    return () => {
      if (chartInstance2D.current) {
        chartInstance2D.current.dispose();
        chartInstance2D.current = null;
      }
      if (chartInstance3D.current) {
        chartInstance3D.current.dispose();
        chartInstance3D.current = null;
      }
    };
  }, []);

  // Additional cleanup when switching views to prevent overlaps
  useEffect(() => {
    if (viewType === '2d-chart' && chartInstance3D.current) {
      chartInstance3D.current.dispose();
      chartInstance3D.current = null;
    }
    if (viewType === '3d-chart' && chartInstance2D.current) {
      chartInstance2D.current.dispose();
      chartInstance2D.current = null;
    }
  }, [viewType]);

  // Resize charts when view changes
  useEffect(() => {
    const handleResize = () => {
      chartInstance2D.current?.resize();
      chartInstance3D.current?.resize();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (viewType === '2d-chart' && chartInstance2D.current) {
        chartInstance2D.current.resize();
      }
      if (viewType === '3d-chart' && chartInstance3D.current) {
        chartInstance3D.current.resize();
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [viewType]);

  if (!isOpen) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'grey.100',
        display: 'flex',
        flexDirection: 'column',
        width: '100vw',
        height: '100vh',
        zIndex: 99999,
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        square
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          px: 3,
          py: 1.75,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'common.white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 2,
              }}
            >
              <BarChart3 size={20} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                Candidate Analytics
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Explore insights from {candidates.length} candidates
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            size="small"
            title="Close Analytics"
            sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'grey.100' } }}
          >
            <X size={20} />
          </IconButton>
        </Stack>
      </Paper>

      {/* View Type Toggle */}
      <Paper
        elevation={0}
        square
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.9)',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          px: 3,
          py: 1.25,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant={viewType === '2d-chart' ? 'contained' : 'outlined'}
              onClick={() => setViewType('2d-chart')}
              sx={{
                fontWeight: 700,
                fontSize: '0.75rem',
                textTransform: 'none',
                px: 2,
                py: 0.5,
              }}
            >
              2D Chart
            </Button>
            <Button
              size="small"
              variant={viewType === '3d-chart' ? 'contained' : 'outlined'}
              onClick={() => setViewType('3d-chart')}
              sx={{
                fontWeight: 700,
                fontSize: '0.75rem',
                textTransform: 'none',
                px: 2,
                py: 0.5,
              }}
            >
              3D Chart
            </Button>
          </Stack>

          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<Filter size={14} />}
            onClick={() => setIsAnalyticalFilter(!isAnalyticalFilter)}
            sx={{
              fontWeight: 700,
              fontSize: '0.75rem',
              textTransform: 'none',
              borderColor: 'grey.300',
              bgcolor: 'background.paper',
            }}
          >
            Filters
          </Button>
        </Stack>
      </Paper>

      {/* Main Chart Area */}
      <Box sx={{ flex: 1, width: '100%', p: { xs: 2, sm: 3 }, overflow: 'hidden', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            flex: 1,
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'grey.200',
            p: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            position: 'relative',
          }}
        >
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(4px)',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 3,
              }}
            >
              <CircularProgress size={32} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mt: 1.5 }}>
                Loading Analytics...
              </Typography>
            </Box>
          )}

          {viewType === '2d-chart' ? (
            <Box
              ref={chart2DRef}
              sx={{ width: '100%', height: '100%', flex: 1, minHeight: 380 }}
            />
          ) : (
            <Box
              ref={chart3DRef}
              sx={{ width: '100%', height: '100%', flex: 1, minHeight: 380 }}
            />
          )}
        </Paper>
      </Box>

      <GraphFilterDrawer
        isOpen={isAnalyticalFilter}
        onClose={() => setIsAnalyticalFilter(false)}
        extractedSkill={extractedSkill}
        setExtractedSkill={setExtractedSkill}
        weightingEnabled={weightingEnabled}
        setWeightingEnabled={setWeightingEnabled}
        similarToggle={similarToggle}
        setSimilarToggle={setSimilarToggle}
        similarJobTitle={similarJobTitle}
        setSimilarJobTitle={setSimilarJobTitle}
        extractedJobTitles={extractedJobTitles}
        setExtractedJobTitles={setExtractedJobTitles}
        extractedBehavioralScore={extractedBehavioralScore}
        setExtractedBehavioralScore={setExtractedBehavioralScore}
        similarBehavior={similarBehavior}
        setSimilarBehavior={setSimilarBehavior}
        extractedKeywords={extractedKeywords}
        setExtractedKeywords={setExtractedKeywords}
        keywordWeightage={keywordWeightage}
        setKeywordWeightage={setKeywordWeightage}
        keywordToggle={keywordToggle}
        setKeywordToggle={setKeywordtoggle}
        searchObject={searchObject}
        setSearchObject={setSearchObject}
        genderList={genderList}
        setGenderList={setGenderList}
        minMaxAge={minMaxAge}
        setMinMaxAge={setMinMaxAge}
        ageToggle={ageToggle}
        setAgeToggle={setAgeToggle}
        setUseSkillPercentage={setUseSkillPercentage}
        statusList={statusList}
        loading={loading}
        setLoading={setLoading}
      />

      {/* Footer */}
      <Paper
        elevation={0}
        square
        sx={{
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'grey.200',
          px: 3,
          py: 1.5,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Data refreshed • Full Screen Analytics View
          </Typography>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              fontWeight: 700,
              fontSize: '0.75rem',
              textTransform: 'none',
              px: 3,
              py: 0.75,
              borderRadius: 2,
            }}
          >
            Close Analytics
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};
